import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { PartnerHomeGuard } from '@app/core/guard/partner-home.guard';
import { featureFlagGuard, featureFlagMatch } from '@app/core/guard/feature-flag.guard';
import { FeatureFlagKeys } from '@services/feature-flags/feature-flag-keys';
import { EconomyHistoryComponent } from './dashboard-management/pages/economy-history/economy-history.component';
import { PermissionGuard } from '@app/core/guard/permission.guard';
import { OnboardingV2Component } from '@app/features/onboarding/v2/onboarding-v2.component';
import { AccountingDocumentManagementComponent } from './accounting-document-management/pages/accounting-document-management/accounting-document-management.component';
import { AccountingDataStandardizationComponent } from './accounting-data-standardization/pages/accounting-data-standardization/accounting-data-standardization.component';
import { InDevelopmentComponent } from '@src/app/features/dashboard/in-development/in-development.component';
import { AttachmentTypeListComponent } from './attachment-type-list/attachment-type-list.component';
import { AttachmentTypeRegisterComponent } from './attachment-type-register/attachment-type-register.component';
import { CompanyListComponent } from './company/company-list/company-list.component';
import { CompanyRegisterComponent } from './company/company-register/company-register.component';
import { CompanyContactsPageComponent } from './company/company-contacts/company-contacts-page.component';
import { CompanyCorporateCharterPageComponent } from './company/corporate-charter/pages/company-corporate-charter-page/company-corporate-charter-page.component';
import { CorporateCharterCompaniesPageComponent } from './company/corporate-charter/pages/corporate-charter-companies-page/corporate-charter-companies-page.component';
import { EconomicGroupsListComponent } from './economic-groups/pages/economic-groups-list/economic-groups-list.component';
import { EconomicGroupBuilderComponent } from './economic-groups/pages/economic-group-builder/economic-group-builder.component';
import { RegisterDealComponent } from './commercial/pages/register-deal/register-deal.component';
import { registerDealLeaveGuard } from './commercial/pages/register-deal/register-deal-leave.guard';
import { MyDealsComponent } from './commercial/pages/my-deals/my-deals.component';
import { PartnerHomeComponent } from './commercial/pages/partner-home/partner-home.component';
import { DealDetailComponent } from './commercial/pages/deal-detail/deal-detail.component';
import { PartnerUsersComponent } from './commercial/pages/partner-users/partner-users.component';
import { ReferralComponent } from './commercial/pages/referral/referral.component';
import { PartnerCalculatorComponent } from './commercial/pages/calculator/partner-calculator.component';
import { CreditCommitteeQueueComponent } from './commercial/pages/credit-committee/credit-committee-queue/credit-committee-queue.component';
import { ClientDealsComponent } from './commercial/pages/client/client-deals/client-deals.component';
import { ClientDealDetailComponent } from './commercial/pages/client/client-deal-detail/client-deal-detail.component';
import { MeetingsComponent } from './meetings/pages/meetings/meetings.component';
import { MeetingDetailComponent } from './meetings/pages/meeting-detail/meeting-detail.component';
import { MeetingSharedLinkComponent } from './meetings/pages/meeting-shared-link/meeting-shared-link.component';
import { ClientMeetingsComponent } from './meetings/pages/client-meetings/client-meetings.component';
import { ClientMeetingDetailComponent } from './meetings/pages/client-meeting-detail/client-meeting-detail.component';
import { clientMeetingsGuard } from './meetings/pages/client-meetings/client-meetings.guard';
import { PartnerListComponent } from './partner/pages/partner-list/partner-list.component';
import { PartnerRegisterComponent } from './partner/pages/partner-register/partner-register.component';
import { PartnerContractTemplateComponent } from './partner/pages/partner-contract-template/partner-contract-template.component';
import { PartnerContractEditorComponent } from './partner/pages/partner-contract-editor/partner-contract-editor.component';
import { partnerContractUnsavedChangesGuard } from './partner/guards/partner-contract-unsaved-changes.guard';
import { ClientContractEditorComponent } from './company/contracts/pages/client-contract-editor/client-contract-editor.component';
import { ClientContractTemplateComponent } from './company/contracts/pages/client-contract-template/client-contract-template.component';
import { ConsolidatedDueDateComponent } from './consolidated-due-date/consolidated-due-date.component';
import { NdfCalculatorListComponent } from './control/derivative-management/derivatives-old-version/ndf-calculator-list/ndf-calculator-list.component';
import { NdfManagementComponent } from './control/derivative-management/derivatives-old-version/ndf-management/ndf-management.component';
import { NdfOperationListComponent } from './control/derivative-management/derivatives-old-version/ndf-operation-list/ndf-operation-list.component';
import { DashboardManagementComponent } from './dashboard-management/dashboard-management.component';
import { DashboardClassicComponent } from './dashboard-management/dashboard-classic.component';
import { DashboardComponent } from './dashboard.component';
import { CurrentUserNotificationsComponent } from './notifications/pages/current-user-notifications/current-user-notifications.component';
import { DocumentAnalysisCompanyComponent } from './document-analysis-company/document-analysis-company.component';
import { DocumentAnalysisComponent } from './document-analysis/document-analysis.component';
import { FinancialInstitutionListComponent } from './financial-institution-list/financial-institution-list.component';
import { FinancialInstitutionRegisterComponent } from './financial-institution-register/financial-institution-register.component';
import { LoanOperationOriginsComponent } from './financial-institution-list/pages/loan-operation-origins/loan-operation-origins.component';
import { GroupListComponent } from './group-list/group-list.component';
import { GroupRegisterComponent } from './group-register/group-register.component';
import { CalendarLabComponent } from './in-development/calendar-lab/calendar-lab.component';
import { FinancialInstitutionSelectLabComponent } from './in-development/financial-institution-select-lab/financial-institution-select-lab.component';
import { InputLabComponent } from './in-development/input-lab/input-lab.component';
import { SelectLabComponent } from './in-development/select-lab/select-lab.component';
import { TenantListComponent } from './tenant-list/tenant-list.component';
import { TenantRegisterComponent } from './tenant-register/tenant-register.component';
import { UserListComponent } from './user-list/user-list.component';
import { UserRegisterComponent } from './user-register/user-register.component';

const routes: Routes = [
  {
    path: '',
    component: DashboardComponent,
    children: [
      {
        path: 'in-development',
        component: InDevelopmentComponent,
      },
      {
        path: 'in-development/select-lab',
        component: SelectLabComponent,
      },
      {
        path: 'in-development/financial-institution-select-lab',
        component: FinancialInstitutionSelectLabComponent,
      },
      {
        path: 'in-development/input-lab',
        component: InputLabComponent,
      },
      {
        path: 'in-development/calendar-lab',
        component: CalendarLabComponent,
      },
      {
        path: 'user',
        loadChildren: () => import('./users/users.module').then(m => m.UsersModule),
      },
      {
        path: 'user/list',
        component: UserListComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.User.LIST },
      },
      {
        path: 'user/register',
        component: UserRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.User.CREATE },
      },
      {
        path: 'user/register/:id',
        component: UserRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.User.UPDATE },
      },
      {
        path: 'user/register/company/:companyId',
        component: UserRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.User.UPDATE },
      },
      {
        path: 'groups/list',
        component: GroupListComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.PermissionGroup.LIST },
      },
      {
        path: 'groups/register',
        component: GroupRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.PermissionGroup.CREATE },
      },
      {
        path: 'groups/register/:id',
        component: GroupRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.PermissionGroup.UPDATE },
      },
      {
        path: 'financial-institution/list',
        component: FinancialInstitutionListComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.FinancialInstitution.LIST },
      },
      {
        path: 'financial-institution/register',
        component: FinancialInstitutionRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.FinancialInstitution.CREATE },
      },
      {
        path: 'financial-institution/register/:id',
        component: FinancialInstitutionRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.FinancialInstitution.UPDATE },
      },
      {
        path: 'financial-institution/:id/loan-operation-origins',
        component: LoanOperationOriginsComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.FinancialInstitution.UPDATE },
      },
      {
        path: 'attachment-type/list',
        component: AttachmentTypeListComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.DerivativeAttachmentType.LIST },
      },
      {
        path: 'attachment-type/register',
        component: AttachmentTypeRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.DerivativeAttachmentType.CREATE },
      },
      {
        path: 'attachment-type/register/:id',
        component: AttachmentTypeRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.DerivativeAttachmentType.UPDATE },
      },
      {
        path: 'tenant/list',
        component: TenantListComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Tenant.LIST },
      },
      {
        path: 'tenant/register',
        component: TenantRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Tenant.CREATE },
      },
      {
        path: 'tenant/register/:id',
        component: TenantRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Tenant.UPDATE },
      },
      {
        path: 'economic-groups',
        component: EconomicGroupsListComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.EconomicGroup.LIST },
      },
      {
        path: 'economic-groups/builder/:id',
        component: EconomicGroupBuilderComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.EconomicGroup.VIEW },
      },
      {
        // Início do parceiro (card 3517): a tela de pouso depois do login (PartnerHomeGuard).
        // Lê o mesmo board de Meus deals, por isso a mesma permissão.
        path: 'commercial/home',
        component: PartnerHomeComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.MY_DEALS },
      },
      {
        // Módulo Comercial (card 2902). Primeira rota do parceiro que não é placeholder.
        path: 'commercial/register-deal',
        component: RegisterDealComponent,
        canActivate: [PermissionGuard],
        // Card 3670: avisa antes de sair com documento ainda subindo.
        canDeactivate: [registerDealLeaveGuard],
        data: { permission: Permissions.Commercial.REGISTER_DEAL },
      },
      {
        // Pipeline arrastável do módulo Comercial (card 2903). Atende parceiro e Legatus;
        // quem recorta o que cada um vê é o backend.
        path: 'commercial/my-deals',
        component: MyDealsComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.MY_DEALS },
      },
      {
        // Tela interna de um deal (card 2952). E para ca que aponta o botao do e-mail de
        // pre-aprovacao, e e dela que sai a solicitacao de assinatura do NDA.
        //
        // A rota fica DEPOIS de 'commercial/my-deals' de proposito: com o parametro antes, o
        // caminho sem id cairia aqui e a listagem nunca abriria.
        path: 'commercial/my-deals/:id',
        component: DealDetailComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.MY_DEALS },
      },
      {
        // Detalhe de uma meeting aberto pela aba Reuniões do deal (card 3162; era "Ligações" até o 3683): também para o
        // parceiro, que só abre as reuniões de que participou (o backend recorta). Só leitura.
        path: 'commercial/my-deals/:dealId/meetings/:meetingId',
        component: MeetingDetailComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.MY_DEALS },
      },
      {
        // Comercial > Usuarios: a equipe do proprio parceiro, administrada pelo master.
        // Gateada por MANAGE_USERS, que vive no grupo de master - usuario comum do parceiro
        // recebe negado aqui e nao ve o item no menu.
        path: 'commercial/users',
        component: PartnerUsersComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.MANAGE_USERS },
      },
      {
        // Calculadora do Parceiro (card 3683): simula flat fee e comissão pelo prazo da captação.
        // A conta é do backend (card 3707); permissão própria, de parceiro e do time interno.
        path: 'commercial/calculator',
        component: PartnerCalculatorComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.CALCULATOR },
      },
      {
        // Comitê de crédito (card 3789): fila dos deals em votação e decididos. Só o time interno.
        // Fica antes de qualquer rota com parâmetro do módulo, como as irmãs.
        path: 'commercial/credit-committee',
        component: CreditCommitteeQueueComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.PartnerDealCommittee.VIEW },
      },
      {
        // A configuração do comitê ficou na Parametrização (card 3789, 03/11/2026); o endereço antigo
        // leva para lá, com a modal do comitê aberta.
        path: 'commercial/credit-committee/settings',
        redirectTo:
          '/dashboard/management/back-office/support/parametrizacao?open=credit-committee',
      },
      {
        // Indique e ganhe (card 3461): o link de indicacao do usuario do parceiro e quem chegou
        // por ele. Gateada por REFERRAL (papel PARTNER); a tela checa o papel, como Usuarios.
        path: 'commercial/referral',
        component: ReferralComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.REFERRAL },
      },
      {
        // Visao do Cliente (card 3461): o usuario da empresa (papel COMPANY) acompanha a propria
        // operacao. Com um deal so, a lista redireciona para o detalhe. O backend recorta pelas
        // empresas do usuario (deal de outra empresa = mesmo erro de inexistente).
        path: 'commercial/client',
        component: ClientDealsComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.CLIENT_VIEW },
      },
      {
        path: 'commercial/client/:id',
        component: ClientDealDetailComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Commercial.CLIENT_VIEW },
      },
      {
        // Ligacoes (dominio Meetings, card 3112): visao geral das reunioes do Teams gravadas
        // pelo Recall.ai; o menu fica no no "Comercial". So do time interno - o guard olha a
        // permissao, e a tela checa o papel TENANT porque o guard nao olha papel.
        path: 'meetings',
        component: MeetingsComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Meeting.LIST },
      },
      {
        // Link de acesso a uma meeting (card 3241): abre, ganha o acesso até o prazo do link e segue
        // para o detalhe. Fica ANTES de 'meetings/:id', senão "compartilhada" viraria o id.
        path: 'meetings/compartilhada/:token',
        component: MeetingSharedLinkComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Meeting.LIST },
      },
      {
        // Detalhe de uma meeting (card 3162): vídeo, download e transcrição ligada ao vídeo. Mesma
        // régua da lista; a reunião privada de quem não participou responde "não encontrada".
        path: 'meetings/:id',
        component: MeetingDetailComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Meeting.LIST },
      },
      {
        // Calls do cliente (card 3803): prontas e DESLIGADAS. Sem item de menu; o guard só deixa
        // entrar com MEETINGVISIBILITY_CLIENT_VIEW e com o parâmetro "Cliente vê calls" ligado
        // (pergunta à própria porta do cliente, que responde "não encontrada" quando desligado).
        path: 'client-meetings',
        component: ClientMeetingsComponent,
        canActivate: [clientMeetingsGuard],
      },
      {
        path: 'client-meetings/:id',
        component: ClientMeetingDetailComponent,
        canActivate: [clientMeetingsGuard],
      },
      {
        path: 'company/list',
        component: CompanyListComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Company.LIST },
      },
      {
        path: 'company/register',
        component: CompanyRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Company.CREATE },
      },
      {
        path: 'company/register/:id',
        component: CompanyRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Company.UPDATE },
      },
      {
        // Contatos da empresa do usuário COMPANY (menu Gerenciamento): ele não vê a listagem nem o cadastro de
        // empresas, então esta é a porta dele para o mesmo painel de contatos. Abrir exige Company.SELF (a leitura);
        // as escritas exigem CompanyContact.MANAGE e o backend confere que a empresa está no escopo do usuário.
        path: 'company/contacts',
        component: CompanyContactsPageComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Company.SELF },
      },
      {
        // Lista "Societário" (menu Empresas > Societário): situação do contrato social de cada empresa do escopo.
        // Mesma permissão da tela da empresa; o papel COMPANY é redirecionado para a própria empresa.
        path: 'company/societario',
        component: CorporateCharterCompaniesPageComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.DataRoom.VIEW },
      },
      {
        // Societário da empresa (PBI 3260): contrato social vigente lido pelo Cog a partir do Data Room.
        // Leitura com a permissão do Data Room, como a spec decidiu (`DataRoomPermissions.VIEW`).
        path: 'company/:id/societario',
        component: CompanyCorporateCharterPageComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.DataRoom.VIEW },
      },
      // Contratos de cliente, Flat Fee e Gestão Ativa (card 3643): os modelos por tipo e o editor da
      // minuta de uma empresa. Pergunta antes de sair com texto não salvo (mesmo guard da parceria).
      {
        path: 'company/contract-templates',
        component: ClientContractTemplateComponent,
        canActivate: [PermissionGuard],
        canDeactivate: [partnerContractUnsavedChangesGuard],
        data: { permission: Permissions.ClientContract.MANAGE_TEMPLATE },
      },
      {
        path: 'company/contracts/:id',
        component: ClientContractEditorComponent,
        canActivate: [PermissionGuard],
        canDeactivate: [partnerContractUnsavedChangesGuard],
        data: { permission: Permissions.ClientContract.VIEW },
      },
      // Parceiros (card 2916). No menu, a lista está em Negociação › Comercial › Parceiros desde o card
      // 3683; o modelo de contrato ficou em Gerenciamento › Parceiros. Mesma forma das rotas de
      // Empresas logo acima: listagem, cadastro e edição, cada uma com a sua permissão.
      {
        path: 'partner/list',
        component: PartnerListComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Partner.LIST },
      },
      {
        path: 'partner/register',
        component: PartnerRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Partner.CREATE },
      },
      {
        path: 'partner/register/:id',
        component: PartnerRegisterComponent,
        canActivate: [PermissionGuard],
        data: { permission: Permissions.Partner.UPDATE },
      },
      // Contrato de Parceria Comercial editável (card 3462): o modelo base versionado e o editor
      // da minuta de cada parceiro. Pergunta antes de sair com texto não salvo.
      {
        path: 'partner/contract-template',
        component: PartnerContractTemplateComponent,
        canActivate: [PermissionGuard],
        canDeactivate: [partnerContractUnsavedChangesGuard],
        data: { permission: Permissions.PartnerContract.MANAGE_TEMPLATE },
      },
      {
        path: 'partner/contracts/:id',
        component: PartnerContractEditorComponent,
        canActivate: [PermissionGuard],
        canDeactivate: [partnerContractUnsavedChangesGuard],
        data: { permission: Permissions.PartnerContract.VIEW },
      },
      {
        path: 'ndf-calculator/list',
        component: NdfCalculatorListComponent,
        data: { permission: Permissions.DerivativeCalculator.MODULE },
      },
      {
        path: 'ndf-management',
        component: NdfManagementComponent,
        data: { permission: Permissions.DerivativeCalculator.MODULE },
      },
      {
        path: 'ndf-management/:id',
        component: NdfManagementComponent,
        data: { permission: Permissions.DerivativeCalculator.MODULE },
      },
      {
        path: 'ndf-operation/list',
        component: NdfOperationListComponent,
        data: { permission: Permissions.DerivativeCalculator.MODULE },
      },
      {
        // Home nova (cards 3786/3788): só para quem tem a flag dashboard-home (Atualizações do
        // Produto; sem cadastro, desligada). Sem a flag, o roteador cai na rota de baixo, a
        // dashboard clássica, na mesma URL.
        path: 'dashboard-management',
        component: DashboardManagementComponent,
        canMatch: [featureFlagMatch(FeatureFlagKeys.DashboardHome)],
        // O parceiro não tem Dashboard (card 3517, 19/10/2026): o guard manda quem tem o
        // papel PARTNER para o Início do Comercial em qualquer navegação (login, logo,
        // casa do breadcrumb, URL salva). Antes (card 2901) a Dashboard era dele.
        canActivate: [PartnerHomeGuard],
      },
      {
        path: 'dashboard-management',
        component: DashboardClassicComponent,
        canActivate: [PartnerHomeGuard],
      },
      {
        // Dashboard clássica sempre disponível, mesmo para quem já vê a Home nova.
        path: 'dashboard-classic',
        component: DashboardClassicComponent,
        canActivate: [PartnerHomeGuard],
        data: {
          breadcrumb: { label: 'Dashboard clássica' },
          title: ['Dashboard clássica'],
        },
      },
      {
        // Histórico da economia ("o que mudou", card 3295): aberto pela modal de operações do
        // cenário de economia; só para quem tem a flag dashboard-economy-history.
        path: 'dashboard-management/economia/historico',
        component: EconomyHistoryComponent,
        canActivate: [
          featureFlagGuard('dashboard-economy-history', '/dashboard/dashboard-management'),
        ],
        data: {
          breadcrumb: { label: 'Histórico da economia' },
          title: ['Histórico da economia'],
        },
      },
      {
        path: 'notifications',
        component: CurrentUserNotificationsComponent,
      },
      {
        path: 'consolidated-due-date',
        component: ConsolidatedDueDateComponent,
      },
      {
        path: 'onboarding/:companyId',
        component: OnboardingV2Component,
      },
      {
        path: 'document-analysis',
        component: DocumentAnalysisComponent,
      },
      {
        path: 'document-analysis-company/:id',
        component: DocumentAnalysisCompanyComponent,
      },
      {
        path: 'accounting-document-management',
        component: AccountingDocumentManagementComponent,
      },
      {
        path: 'accounting-data-standardization',
        component: AccountingDataStandardizationComponent,
      },
      {
        path: 'derivative-assets', // TODO: MUDAR derivative-assets para o padrão das rotas que estão abaixo dela
        data: {
          breadcrumb: {
            label: 'management-reports',
            url: 'reports',
          },
          permission: '',
          title: ['Reports'],
        },
        loadChildren: () =>
          import('./control/derivative-management/reports/derivative-assets/derivative-assets.module').then(
            m => m.DerivativeAssetsModule
          ),
      },
      {
        path: 'ai',
        loadChildren: () => import('./ai/ai.module').then(m => m.AiModule),
      },

      {
        path: 'document-padronization',
        loadChildren: () =>
          import('./document-padronization/document-padronization.module').then(
            m => m.DocumentPadronizationModule
          ),
      },
      {
        path: 'comex-operation',
        loadChildren: () =>
          import('./comex/comex-operation.module').then(m => m.ComexOperationModule),
      },
      {
        path: 'market',
        loadChildren: () => import('./market/market.module').then(m => m.MarketModule),
      },
      {
        path: 'management',
        loadChildren: () => import('./management/management.module').then(m => m.ManagementModule),
      },
      {
        path: 'costs',
        loadChildren: () => import('./costs/costs.module').then(m => m.CostsModule),
      },
      {
        path: 'performance',
        loadChildren: () =>
          import('./performance/performance.module').then(m => m.PerformanceModule),
      },
      {
        path: 'control',
        data: {
          breadcrumb: {
            label: 'Controle',
          },
        },
        loadChildren: () => import('./control/control.module').then(m => m.ControlModule),
      },
      {
        path: 'negotiation',
        loadChildren: () =>
          import('./negotiation/negotiation.module').then(m => m.NegotiationModule),
      },
      {
        path: 'business-case',
        loadChildren: () =>
          import('./business-case/business-case.module').then(m => m.BusinessCaseModule),
      },
      {
        path: 'mcp',
        loadChildren: () => import('./mcp/mcp.module').then(m => m.McpModule),
      },
      {
        path: 'feature',
        loadChildren: () => import('./feature/feature.module').then(m => m.FeatureModule),
      },
      { path: '', redirectTo: 'dashboard-management', pathMatch: 'full' },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class DashboardRoutingModule {}
